import { useState, useEffect } from 'react';
import { useNavigate, NavLink, generatePath } from 'react-router-dom';
import {
  IconSelected,
  IconAttention,
  IconBlock,
  Checkbox,
  ClickOutsideDetector,
  IconChat,
  IconDownload,
  IconLoader,
  PermissionEnum,
  IconEdit,
  // IconTrash,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  useLazyGetCorporateApplicationDocumentFileQuery,
  useChangeCorporateApplicationDocumentStatusMutation,
  useLazyGetIndividualApplicationDocumentFileQuery,
  useChangeIndividualApplicationDocumentStatusMutation,
  useLazyGetCofApplicationDocumentFileQuery,
  useChangeCOFApplicationDocumentStatusMutation,
} from '../../../hoc/new-application/store/services';
import {
  ApplicationDocumentStatus,
  NewApplicationStatus,
} from '../../../hoc/new-application/types';
import { DocumentItemTypes } from '../types';

import ROUTES from '../../../routes';

import cn from '../new-application.module.css';

const DocumentItem = ({
  document: documentItem,
  applicationId,
  onSelect,
  applicationStatus,
  search,
  setRenamePopupVisible,
  setSelectedDocument,
  renameAvailable,
  isIndividual,
  isCof,
}: DocumentItemTypes) => {
  const [menuVisible, setMenuVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<
    ApplicationDocumentStatus | undefined
  >();

  const [
    getCorporateApplicationDocumentFile,
    { isLoading, isSuccess, isError },
  ] = useLazyGetCorporateApplicationDocumentFileQuery();
  const [
    changeCorporateDocumentStatus,
    { isLoading: statusChangeInProgress, isSuccess: statusChangeIsSuccess },
  ] = useChangeCorporateApplicationDocumentStatusMutation();

  const [
    getIndividualApplicationDocumentFile,
    {
      isLoading: individualIsLoading,
      isSuccess: individualIsSuccess,
      isError: individualIsError,
    },
  ] = useLazyGetIndividualApplicationDocumentFileQuery();
  const [
    changeIndividualDocumentStatus,
    {
      isLoading: individualStatusChangeInProgress,
      isSuccess: individualStatusChangeIsSuccess,
    },
  ] = useChangeIndividualApplicationDocumentStatusMutation();

  const [
    getCofApplicationDocumentFile,
    { isLoading: cofIsLoading, isSuccess: cofIsSuccess, isError: cofIsError },
  ] = useLazyGetCofApplicationDocumentFileQuery();
  const [
    changeCofDocumentStatus,
    {
      isLoading: cofStatusChangeInProgress,
      isSuccess: cofStatusChangeIsSuccess,
    },
  ] = useChangeCOFApplicationDocumentStatusMutation();

  const { activePermissions } = useAppSelector(
    (state) => state.customersDictionaries,
  );

  const navigate = useNavigate();

  const getFile = async () => {
    let response;

    if (!isIndividual && !isCof) {
      response = await getCorporateApplicationDocumentFile({
        id: applicationId as string,
        documentId: documentItem.id,
      });
    } else if (isIndividual) {
      response = await getIndividualApplicationDocumentFile({
        id: applicationId as string,
        documentId: documentItem.id,
      });
    } else if (isCof) {
      response = await getCofApplicationDocumentFile({
        id: applicationId as string,
        documentId: documentItem.id,
      });
    }

    if (response?.data) {
      const hiddenElement = document.createElement('a');
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = documentItem.name;
      hiddenElement.click();
    }
  };

  useEffect(() => {
    if (
      isSuccess ||
      individualIsSuccess ||
      cofIsSuccess ||
      isError ||
      individualIsError ||
      cofIsError ||
      statusChangeIsSuccess ||
      individualStatusChangeIsSuccess ||
      cofStatusChangeIsSuccess
    ) {
      setMenuVisible(false);

      if (statusChangeIsSuccess) {
        setSelectedStatus(undefined);
      }
    }
  }, [
    isSuccess,
    isError,
    statusChangeIsSuccess,
    individualIsSuccess,
    cofIsSuccess,
    individualIsError,
    cofIsError,
    individualStatusChangeIsSuccess,
    cofStatusChangeIsSuccess,
  ]);

  let statusIcon;

  if (documentItem.status === ApplicationDocumentStatus.ACCEPTED) {
    statusIcon = (
      <div className={cn.documentRowAccepted}>
        <IconSelected
          className={cn.documentRowStatusIcon}
          height={9}
          width={13}
        />
      </div>
    );
  } else if (documentItem.status === ApplicationDocumentStatus.EXPIRED) {
    statusIcon = (
      <div className={`${cn.documentRowExpired} ${cn.withoutTransform}`}>
        <IconAttention />
      </div>
    );
  } else if (documentItem.status === ApplicationDocumentStatus.REJECTED) {
    statusIcon = (
      <div className={cn.documentRowRejected}>
        <IconBlock />
      </div>
    );
  } else {
    statusIcon = <div className={cn.documentRowNew} />;
  }

  let documentLink = ROUTES.CUSTOMERS_NEW_APPLICATION_CORPORATE_DOCUMENT;

  if (isIndividual) {
    documentLink = ROUTES.CUSTOMERS_NEW_APPLICATION_INDIVIDUAL_DOCUMENT;
  } else if (isCof) {
    documentLink = ROUTES.CUSTOMERS_NEW_APPLICATION_COMPANY_OFFICIAL_DOCUMENT;
  }

  const link = generatePath(documentLink, {
    documentId: documentItem.id,
    applicationId: applicationId as string,
  });

  return (
    <div className={cn.documentRowItem}>
      <NavLink
        to={`${link}${search || ''}`}
        className={cn.documentLink}
        tabIndex={-1}
      />
      <div className={cn.documentRowName}>
        <Checkbox
          name={`select-document-${documentItem?.id}`}
          type="checkbox"
          title={documentItem.name}
          checked={documentItem.isSelected}
          onChange={(e) => onSelect(e.target.checked, documentItem.id)}
          partialDisabled={Boolean(
            documentItem?.documentCategoryId?.includes('identification'),
          )}
          tabIndex={-1}
        />
      </div>
      <div className={cn.documentRowStatus}>
        <div className={cn.documentRowStatusLeft}>
          <div className={cn.documentRowStatusLeftItem}>
            {documentItem.status}
          </div>
          <div className={cn.documentRowStatusLeftItem}>{statusIcon}</div>
          <div className={cn.documentRowStatusLeftItem}>
            {documentItem?.countComments &&
              documentItem?.countComments.count > 0 && (
                <div className={cn.documentRowCommentIndiciator}>
                  <IconChat
                    className={cn.documentsRowCommentIcon}
                    width={14}
                    height={14}
                  />
                  {documentItem?.countComments.count}
                </div>
              )}
          </div>
        </div>
        <ClickOutsideDetector setOptionsListVisible={setMenuVisible}>
          <>
            <button
              type="button"
              className={`${cn.documentMenuButton} ${
                menuVisible ? cn.active : ''
              }`}
              aria-label="Document menu button"
              onClick={() => setMenuVisible(!menuVisible)}
              tabIndex={-1}
            >
              <span />
            </button>
            {menuVisible && (
              <ul className={cn.documentMenu}>
                {applicationStatus !== NewApplicationStatus.ACCEPTED &&
                  applicationStatus !== NewApplicationStatus.DECLINED &&
                  activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_UPDATE_STATUS_ACTION,
                  ) &&
                  !documentItem?.documentCategoryId?.includes(
                    'identification',
                  ) && (
                    <>
                      {documentItem.status !==
                        ApplicationDocumentStatus.ACCEPTED &&
                        documentItem?.status !==
                          ApplicationDocumentStatus.EXPIRED && (
                          <li>
                            <button
                              type="button"
                              className={cn.documentMenuItem}
                              tabIndex={-1}
                              onClick={() => {
                                setSelectedStatus(
                                  ApplicationDocumentStatus.ACCEPTED,
                                );

                                if (!isCof && !isIndividual) {
                                  changeCorporateDocumentStatus({
                                    id: applicationId as string,
                                    body: {
                                      ids: [documentItem.id],
                                      status:
                                        ApplicationDocumentStatus.ACCEPTED,
                                    },
                                  });
                                } else if (isIndividual) {
                                  changeIndividualDocumentStatus({
                                    id: applicationId as string,
                                    body: {
                                      ids: [documentItem.id],
                                      status:
                                        ApplicationDocumentStatus.ACCEPTED,
                                    },
                                  });
                                } else if (isCof) {
                                  changeCofDocumentStatus({
                                    id: applicationId as string,
                                    body: {
                                      ids: [documentItem.id],
                                      status:
                                        ApplicationDocumentStatus.ACCEPTED,
                                    },
                                  });
                                }
                              }}
                              disabled={
                                statusChangeInProgress ||
                                individualStatusChangeInProgress ||
                                cofStatusChangeInProgress
                              }
                            >
                              <div>
                                {(statusChangeInProgress ||
                                  individualStatusChangeInProgress ||
                                  cofStatusChangeInProgress) &&
                                selectedStatus ===
                                  ApplicationDocumentStatus.ACCEPTED ? (
                                  <IconLoader />
                                ) : (
                                  <IconSelected />
                                )}
                              </div>
                              {(statusChangeInProgress ||
                                individualStatusChangeInProgress ||
                                cofStatusChangeInProgress) &&
                              selectedStatus ===
                                ApplicationDocumentStatus.ACCEPTED
                                ? 'Loading...'
                                : 'Accept'}
                            </button>
                          </li>
                        )}
                      {documentItem.status !==
                        ApplicationDocumentStatus.REJECTED &&
                        documentItem?.status !==
                          ApplicationDocumentStatus.EXPIRED && (
                          <li>
                            <button
                              type="button"
                              className={cn.documentMenuItem}
                              onClick={() => {
                                setSelectedStatus(
                                  ApplicationDocumentStatus.REJECTED,
                                );

                                if (!isIndividual && !isCof) {
                                  changeCorporateDocumentStatus({
                                    id: applicationId as string,
                                    body: {
                                      ids: [documentItem.id],
                                      status:
                                        ApplicationDocumentStatus.REJECTED,
                                    },
                                  });
                                } else if (isIndividual) {
                                  changeIndividualDocumentStatus({
                                    id: applicationId as string,
                                    body: {
                                      ids: [documentItem.id],
                                      status:
                                        ApplicationDocumentStatus.REJECTED,
                                    },
                                  });
                                } else if (isCof) {
                                  changeCofDocumentStatus({
                                    id: applicationId as string,
                                    body: {
                                      ids: [documentItem.id],
                                      status:
                                        ApplicationDocumentStatus.REJECTED,
                                    },
                                  });
                                }
                              }}
                              disabled={
                                statusChangeInProgress ||
                                cofStatusChangeInProgress ||
                                individualStatusChangeInProgress
                              }
                              tabIndex={-1}
                            >
                              <div>
                                {(statusChangeInProgress ||
                                  cofStatusChangeInProgress ||
                                  individualStatusChangeInProgress) &&
                                selectedStatus ===
                                  ApplicationDocumentStatus.REJECTED ? (
                                  <IconLoader />
                                ) : (
                                  <IconBlock />
                                )}
                              </div>

                              {(statusChangeInProgress ||
                                cofStatusChangeInProgress ||
                                individualStatusChangeInProgress) &&
                              selectedStatus ===
                                ApplicationDocumentStatus.REJECTED
                                ? 'Loading...'
                                : 'Reject'}
                            </button>
                          </li>
                        )}
                    </>
                  )}
                {renameAvailable && (
                  <li>
                    <button
                      type="button"
                      className={cn.documentMenuItem}
                      onClick={() => {
                        setRenamePopupVisible(true);
                        setSelectedDocument(documentItem);
                      }}
                      disabled={
                        statusChangeInProgress ||
                        cofStatusChangeInProgress ||
                        individualStatusChangeInProgress
                      }
                      tabIndex={-1}
                    >
                      <IconEdit />
                      Rename
                    </button>
                  </li>
                )}
                <li>
                  <button
                    type="button"
                    className={cn.documentMenuItem}
                    onClick={() => navigate(`${link}${search || ''}`)}
                    tabIndex={-1}
                  >
                    <div>
                      <IconChat />
                    </div>
                    Comments
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    className={cn.documentMenuItem}
                    disabled={isLoading || individualIsLoading || cofIsLoading}
                    onClick={getFile}
                    tabIndex={-1}
                  >
                    <div>
                      {isLoading || individualIsLoading || cofIsLoading ? (
                        <IconLoader />
                      ) : (
                        <IconDownload />
                      )}
                    </div>
                    Downloads
                  </button>
                </li>
                {/* {
                  applicationStatus !== NewApplicationStatus.ACCEPTED &&
                  applicationStatus !== NewApplicationStatus.DECLINED &&
                  activePermissions?.includes(
                    PermissionEnum.APPLICATIONS_CUSTOMERS_DOCUMENTS_DELETE_ACTION,
                  ) && (
                    <li>
                    <button
                      type="button"
                      className={cn.documentMenuItem}
                      onClick={() => {
                        setMenuVisible(false);
                      }}
                    >
                      <div>
                        <IconTrash />
                      </div>
                      Delete
                    </button>
                  </li>
                  )
                } */}
              </ul>
            )}
          </>
        </ClickOutsideDetector>
      </div>
    </div>
  );
};

export default DocumentItem;
